import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Mail, Phone, MapPin, Send, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';

export default function ContactPage() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitted(true);
    toast.success('Message sent! We will get back to you soon.');
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <SEO title="Contact Us" description="Get in touch with the Training Solutions team. We're here to help trainers and companies connect." path="/contact" />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <span className="font-outfit font-bold text-xl text-slate-900">Training Solutions</span>
            </Link>
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/search?type=trainers" className="text-slate-600 hover:text-slate-900 font-medium">Find Trainers</Link>
              <Link to="/about" className="text-slate-600 hover:text-slate-900 font-medium">About</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900 font-medium">Pricing</Link>
            </nav>
            <Link to="/register">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-16">
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-16">
              <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-slate-900 mb-4">Get in Touch</h1>
              <p className="text-lg text-slate-500 max-w-xl mx-auto">Have a question, feedback, or need support? We'd love to hear from you.</p>
            </div>

            <div className="grid md:grid-cols-5 gap-12">
              {/* Contact Info */}
              <div className="md:col-span-2 space-y-8">
                <div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-6">Contact Information</h3>
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">Email</p>
                        <a href="mailto:Hello@trainingsolutions.in" className="text-blue-600 hover:underline text-sm">Hello@trainingsolutions.in</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">Phone</p>
                        <a href="tel:+919999988439" className="text-slate-600 hover:text-blue-600 text-sm">+91 99999 88439</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">Address</p>
                        <p className="text-slate-600 text-sm">Training Solutions<br />Chhatarpur, Delhi 110068, India</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-6">
                  <h4 className="font-semibold text-blue-900 mb-2">Business Hours</h4>
                  <p className="text-sm text-blue-800">Monday – Friday: 9:00 AM – 6:00 PM IST</p>
                  <p className="text-sm text-blue-800">Saturday: 10:00 AM – 2:00 PM IST</p>
                  <p className="text-sm text-blue-700 mt-2">Response time: Within 24 hours</p>
                </div>
              </div>

              {/* Form */}
              <div className="md:col-span-3">
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-8 space-y-6">
                    <h3 className="font-outfit text-xl font-semibold text-slate-900 flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-blue-600" /> Send us a Message
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Name *</Label>
                        <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Your name" className="mt-2" data-testid="contact-name" />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="you@example.com" className="mt-2" data-testid="contact-email" />
                      </div>
                    </div>
                    <div>
                      <Label>Subject</Label>
                      <Input value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} placeholder="What's this about?" className="mt-2" data-testid="contact-subject" />
                    </div>
                    <div>
                      <Label>Message *</Label>
                      <Textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="Tell us how we can help..." rows={5} className="mt-2" data-testid="contact-message" />
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 rounded-lg" data-testid="contact-submit-btn">
                      <Send className="w-4 h-4 mr-2" /> Send Message
                    </Button>
                  </form>
                ) : (
                  <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-12 text-center" data-testid="contact-success">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Send className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h3 className="font-outfit text-2xl font-bold text-emerald-900 mb-2">Message Sent!</h3>
                    <p className="text-emerald-700">Thank you for reaching out. Our team will respond within 24 hours.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-sm text-slate-400">© {new Date().getFullYear()} Training Solutions. All rights reserved.</p>
          </div>
        </footer>
      </main>
    </div>
  );
}
